import { useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useLanguage } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-[#E2E7F7] bg-white px-4 py-3 text-sm text-[#0F2645] placeholder:text-[#5C6A87] focus:outline-none focus:ring-2 focus:ring-[#3A5CC8] focus:border-[#3A5CC8] transition-colors duration-200";

export default function ResetPassword() {
  const { t } = useLanguage();
  const { token } = useParams();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const canSubmit = password.length >= 6 && password === confirm;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setDone(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : t("Réinitialisation impossible. Faites une nouvelle demande."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reset-page" className="font-body bg-[#F6F8FD] text-[#0F2645] min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 min-h-screen flex items-center justify-center px-6 pt-28 pb-16">
        <div className="w-full max-w-md">
          <p className="text-sm font-bold tracking-[0.1em] uppercase text-[#3A5CC8] text-center">{t("Espace membres")}</p>
          <h1 data-testid="reset-title" className="mt-4 font-heading text-2xl sm:text-3xl font-bold tracking-tight text-center">
            {t("Nouveau mot de passe")}
          </h1>
          {done ? (
            <div data-testid="reset-success" className="mt-8 rounded-2xl border border-[#E2E7F7] bg-white shadow-sm p-6 sm:p-8 text-center">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto" />
              <p className="mt-4 text-sm text-[#5C6A87] leading-relaxed">
                {t("Votre mot de passe a été mis à jour. Vous pouvez maintenant vous connecter.")}
              </p>
              <Link
                to="/connexion"
                data-testid="reset-login-btn"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#3A5CC8] px-8 py-3.5 text-sm font-semibold text-white hover:bg-[#3A5CC8] transition-colors duration-200"
              >
                {t("Se connecter")} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 rounded-2xl border border-[#E2E7F7] bg-white shadow-sm p-6 sm:p-8 space-y-5">
              <div>
                <label htmlFor="reset-password" className="block text-sm font-medium text-[#0F2645] mb-2">{t("Nouveau mot de passe")}</label>
                <input id="reset-password" type="password" data-testid="reset-password" className={inputCls} placeholder={t("6 caractères minimum")} value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
              <div>
                <label htmlFor="reset-confirm" className="block text-sm font-medium text-[#0F2645] mb-2">{t("Confirmer le mot de passe")}</label>
                <input id="reset-confirm" type="password" data-testid="reset-confirm" className={inputCls} placeholder={t("Retapez le mot de passe")} value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
                {confirm && password !== confirm && (
                  <p data-testid="reset-mismatch" className="mt-2 text-xs text-red-600">{t("Les mots de passe ne correspondent pas")}</p>
                )}
              </div>
              {error && <p data-testid="reset-error" className="text-sm text-red-600">{error}</p>}
              <button
                type="submit"
                data-testid="reset-submit-btn"
                disabled={!canSubmit || loading}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#3A5CC8] px-8 py-3.5 text-sm font-semibold text-white shadow-[0_6px_16px_-8px_rgba(58,92,200,0.5)] hover:bg-[#3A5CC8] disabled:opacity-40 disabled:pointer-events-none transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#3A5CC8]"
              >
                {loading ? t("Mise à jour…") : t("Mettre à jour mon mot de passe")} <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
